// useVoiceRecorder — ticket 2's shared recorder, extended for ticket 6b.
//
// Identical file in both repos (reader: src/hooks/useVoiceRecorder.js,
// author: src/hooks/useVoiceRecorder.js) — same pattern already used for
// lib/tropeUtils.js. One implementation, not two that drift apart.
//
// Two callers, two shapes, same hook -- extended with options rather than
// forked, because the difference between them is entirely parametric, not
// a difference in what recording actually requires:
//   - reanlo-author's AddBook (ticket 2, unchanged): short recordings,
//     capped at 55s, transcribed immediately on stop(), audio discarded.
//     Calls useVoiceRecorder() with no options -- every default below
//     reproduces its exact prior behaviour, so this file's rebuild is a
//     no-op for that caller.
//   - reanlo-reader's journal (ticket 6b): recordings up to 3 minutes,
//     which Google's synchronous speech:recognize (the only thing
//     voice-to-text/index.ts speaks) cannot transcribe at all -- it caps
//     near 60s/10MB. So stop() here does not attempt to transcribe a
//     keepAudio recording; it hands back the raw blob, and transcribing
//     it via speech:longrunningrecognize is a separate flow the caller
//     drives itself, after the blob is uploaded (spec 9.3) -- not
//     something this hook can do inline the way the short recordings do.
//   Pass { maxDurationMs: 180_000, keepAudio: true } for the journal case.
//
// Records to WAV (16-bit PCM) via the Web Audio API rather than
// MediaRecorder. Not the obvious choice — MediaRecorder is simpler — but
// MediaRecorder's container is browser-dependent (WebM/Opus on
// Chrome/Firefox/Edge/Android, MP4/AAC on Safari, since Safari has no
// WebM support at all), and Google's Speech-to-Text v1 has no MP4/AAC
// encoding option. WAV sidesteps the whole cross-browser gap: every
// browser can be driven through AudioContext + ScriptProcessorNode to
// produce raw PCM, and Google auto-detects encoding and sample rate from
// a WAV file's own header, so neither edge function ever has to know
// which browser recorded it. See supabase/functions/voice-to-text/index.ts
// for the server side of this same reasoning.
//
// ScriptProcessorNode is deprecated in favor of AudioWorklet, used here
// anyway: it needs no separate worklet module file to ship and load, it
// works in every browser this app already supports, and nothing here is
// latency-sensitive enough for AudioWorklet's advantage (running off the
// main thread) to matter. Worth revisiting if that stops being true.
//
// "Audio transcribed and discarded, never stored" -- the guarantee ticket
// 2 documented here -- now only holds for the default (keepAudio: false)
// path, and it still holds absolutely for that path: nothing in this file
// persists a short recording anywhere, and the default caller (AddBook)
// never sets keepAudio. keepAudio: true is an explicit, deliberate opt-in
// a caller must request by name -- it does not change what happens to
// anyone who didn't ask for it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

// Leaves 5s of margin under Google's hard 60s/10MB sync-recognize cap
// (research doc question 4; confirmed directly against Google's own
// sync-recognize docs). The edge function also enforces a byte-size
// backstop server-side — this is the primary limit, that one is defense
// in depth against a modified or third-party caller.
//
// This is a DEFAULT now, not a hard ceiling -- a caller passes its own
// maxDurationMs to record longer (the journal's 3 minutes, ticket 6b
// spec 9.1/9.8). Nothing about the 55s number itself changes for a
// caller that doesn't ask for something different.
const DEFAULT_MAX_DURATION_MS = 55_000;

// ── Network deadline for the transcribe call ────────────────────────────
//
// supabase.functions.invoke has no deadline of its own unless one is
// passed, and the edge function's own inner budgets (OAuth exchange, then
// recognize) run sequentially — a slow-but-real run can take as long as
// both put together before returning anything. Without an outer bound
// here, a hung or slow call left the caller watching "transcribing"
// forever with no failure path at all.
//
// Mirrors GOOGLE_OAUTH_TIMEOUT_MS in
// supabase/functions/voice-to-text/index.ts. Kept as a literal, not
// fetched from anywhere — these two values are read by two different
// runtimes (browser vs Deno) that share no config, so the comment on both
// sides is what has to catch a drift, not the code.
//
// Only relevant to the autoTranscribe path (keepAudio: false, the
// default) -- a keepAudio caller never reaches this invoke() call at all.
//
// The invoke() `timeout` option below this depends on is not guaranteed by
// package.json's "^2.39.0" range alone — that only requires 2.39.0 or
// later, and older 2.x releases don't have it. It works today because each
// repo's lockfile happens to pin a materially newer @supabase/supabase-js
// (confirmed by reading node_modules/@supabase/functions-js's compiled
// source directly, not by trusting the type surface). If a lockfile is
// ever regenerated loosely or dropped, this deadline stops existing
// silently — no error, no failing check, just the old unbounded hang
// coming back. Whoever next touches this dependency's version needs a
// reason to check for that; this comment is it.
const GOOGLE_OAUTH_TIMEOUT_MS_MIRROR = 8000;

// Same reasoning as UPSTREAM_TIMEOUT_MS sitting inside
// EXTERNAL_SEARCH_TIMEOUT_MS in book-search: the outer deadline has to
// leave room for the hop back after the inner work finishes, not equal it
// exactly.
const INVOKE_ROUND_TRIP_MARGIN_MS = 3000;

// What a caller gets if it doesn't pass transcribeTimeoutMs to
// useVoiceRecorder() — mirrors GOOGLE_RECOGNIZE_TIMEOUT_DEFAULT_MS on the
// server, i.e. the reader journal's budget, since it's the only caller
// wired to a screen today. A future caller with a tighter budget (the
// craving box's ~4s share of its 8s total, per the research doc) passes
// its own value instead of taking this one.
const DEFAULT_TRANSCRIBE_TIMEOUT_MS = 20_000;

// Mirrors GOOGLE_RECOGNIZE_TIMEOUT_MIN_MS / _MAX_MS in
// supabase/functions/voice-to-text/index.ts. The server clamps whatever it
// receives to this same range regardless, but without clamping here too, a
// caller passing something above the server's max (say 60_000) would make
// the browser wait far longer than the function will ever actually spend —
// the same two-sided-number shape the outer/inner deadline pair above
// exists to avoid, just on the timeout value itself rather than the total
// wait.
const GOOGLE_RECOGNIZE_TIMEOUT_MIN_MS_MIRROR = 2000;
const GOOGLE_RECOGNIZE_TIMEOUT_MAX_MS_MIRROR = 20000;

function resolveTranscribeTimeoutMs(requested) {
  if (typeof requested !== 'number' || !Number.isFinite(requested)) {
    return DEFAULT_TRANSCRIBE_TIMEOUT_MS;
  }
  return Math.min(
    GOOGLE_RECOGNIZE_TIMEOUT_MAX_MS_MIRROR,
    Math.max(GOOGLE_RECOGNIZE_TIMEOUT_MIN_MS_MIRROR, Math.floor(requested)),
  );
}

// How many recent amplitude samples the live waveform keeps. Refreshed on
// the same 250ms tick elapsedMs already uses (see start()) rather than on
// every onaudioprocess callback (~85ms at 48kHz) -- a waveform bar does
// not need to move faster than that, and re-rendering at audio-callback
// speed would cost far more than the feature is worth. 40 samples at
// 250ms each is 10 seconds of visible history, which is plenty for a
// small live bar graph without the array becoming a memory or diffing
// concern over a 3-minute recording.
const WAVEFORM_HISTORY_LENGTH = 40;

// Google's speech:recognize wants 16kHz and silently downsamples anything
// higher on ITS end -- after we've already paid to upload the bigger file.
// Desktop hardware defaults to 44.1kHz or 48kHz, so recording at native
// rate and uploading that sends up to 3x the bytes of a 16kHz WAV for zero
// transcription benefit. Confirmed live: a ~10s craving recorded at native
// rate was measured well over 1MB of base64 payload; at 16kHz the same
// clip is roughly a third of that. Only the transcribe-and-discard path
// (keepAudio: false) uses this -- the journal's kept recordings are
// stored for playback and stay at native quality (see start()/stop()).
const TRANSCRIBE_SAMPLE_RATE_HZ = 16000;

// ── WAV encoding ─────────────────────────────────────────────────────────

// Linear-interpolation downsample. Not audiophile-grade, but speech
// recognition doesn't need audiophile-grade -- Google's own sync
// recognize endpoint downsamples on its end with no special algorithm
// called out either. Only ever called going DOWN in rate (native hardware
// rate, e.g. 48000, to TRANSCRIBE_SAMPLE_RATE_HZ's 16000): the fallback
// path for a browser whose AudioContext refused the requested rate
// outright (see start()).
function resampleFloat32(input, fromRate, toRate) {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const srcFloor = Math.floor(srcIndex);
    const srcCeil = Math.min(srcFloor + 1, input.length - 1);
    const frac = srcIndex - srcFloor;
    output[i] = input[srcFloor] * (1 - frac) + input[srcCeil] * frac;
  }
  return output;
}

function floatTo16BitPCM(view, offset, input) {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

// samples: a single Float32Array of the whole recording (mono), at
// whatever sampleRate the caller hands in. This encoder itself does no
// resampling -- it just writes whatever it's given. The resampling
// decision (native quality for keepAudio, TRANSCRIBE_SAMPLE_RATE_HZ for
// the transcribe-and-discard path) is made by the caller in stop(),
// below. An earlier version of this comment said native rate was used
// unconditionally and the only cost was "a slightly larger payload" --
// true for the journal's kept recordings, not true once a caller
// (ticket 21's craving box) is on a latency budget instead.
function encodeWav(samples, sampleRate) {
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample; // mono
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);           // PCM fmt chunk size
  view.setUint16(20, 1, true);            // audioFormat = 1 (PCM)
  view.setUint16(22, 1, true);            // numChannels = 1 (mono)
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // byteRate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);           // bitsPerSample
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);
  floatTo16BitPCM(view, 44, samples);

  return new Blob([buffer], { type: 'audio/wav' });
}

// Chunked to avoid spreading a large byte array through
// String.fromCharCode(...bytes) in one call, which blows the call stack
// on anything more than a few seconds of audio. Only ever called on the
// autoTranscribe path now -- a keepAudio caller hands the blob to GCS via
// a signed PUT (src/lib/voiceNoteStorage.js) and never needs it as base64
// at all.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read recorded audio.'));
    reader.readAsDataURL(blob);
  });
}

// ── The hook ─────────────────────────────────────────────────────────────
//
// State machine: idle → recording → transcribing → idle (or error at any
// point). Callers read `state` for UI and call start()/stop()/cancel().
//
// stop()'s return value depends on keepAudio:
//   - keepAudio: false (default, AddBook's case) — resolves to the
//     transcript string, exactly as before. "transcript returns to the
//     field for correction before anything saves" is the caller's job,
//     not this hook's.
//   - keepAudio: true (the journal's case) — resolves to
//     { blob, durationSeconds }. No transcript field: transcribing a
//     recording this long is a separate flow this hook does not run (see
//     the header comment), so there is nothing to put in one.
// Either way this hook never writes anything anywhere on its own -- what
// the caller does with the resolved value, including whether and how it
// uploads a kept blob, is entirely outside this file.
//
// phaseRef mirrors the reactive `state` and is what start()/stop() guard
// on internally, instead of reading `state` from their own closures.
// Reason: the auto-stop timer is a setTimeout created inside start(),
// which fires later and calls stop() — but if stop() were recreated
// every render (useCallback depending on `state`, as a first pass at
// this did), the closure start() captured at creation time is *that
// render's* stop(), whose own internal `state` check is frozen at
// whatever `state` was when start() ran (still 'idle', a render before
// setState('recording') took effect). The guard would then read stale
// 'idle' forever and the timer would silently no-op instead of stopping
// the recording. A ref sidesteps this: whichever closure reads
// phaseRef.current always sees the true current phase, regardless of
// which render created that closure.
//
// maxDurationMs, transcribeTimeoutMs and keepAudio all go through the
// same ref-mirroring pattern for the same reason: stop() and start() are
// built once (useCallback with no reactive deps on these values) so that
// the auto-stop timer's captured closure never goes stale the way the
// phaseRef comment above describes -- so any option that could vary
// between renders has to be read through a ref, not a plain closed-over
// variable, or it would silently freeze at whatever value was current
// when start()/stop() were first created.
export function useVoiceRecorder({
  transcribeTimeoutMs = DEFAULT_TRANSCRIBE_TIMEOUT_MS,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
  keepAudio = false,
} = {}) {
  const [state, setState] = useState('idle'); // idle | recording | transcribing | error
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [waveform, setWaveform] = useState(() => new Array(WAVEFORM_HISTORY_LENGTH).fill(0));

  const phaseRef = useRef('idle');
  const setPhase = useCallback((next) => {
    phaseRef.current = next;
    setState(next);
  }, []);

  const transcribeTimeoutMsRef = useRef(resolveTranscribeTimeoutMs(transcribeTimeoutMs));
  useEffect(() => {
    transcribeTimeoutMsRef.current = resolveTranscribeTimeoutMs(transcribeTimeoutMs);
  }, [transcribeTimeoutMs]);

  const maxDurationMsRef = useRef(maxDurationMs);
  useEffect(() => {
    maxDurationMsRef.current = maxDurationMs;
  }, [maxDurationMs]);

  const keepAudioRef = useRef(keepAudio);
  useEffect(() => {
    keepAudioRef.current = keepAudio;
  }, [keepAudio]);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickIntervalRef = useRef(null);
  const autoStopTimeoutRef = useRef(null);

  // Updated on every onaudioprocess callback (see start()), read only by
  // the 250ms tick below -- never itself triggers a render.
  const levelRef = useRef(0);

  const teardownAudioGraph = useCallback(() => {
    if (tickIntervalRef.current) { clearInterval(tickIntervalRef.current); tickIntervalRef.current = null; }
    if (autoStopTimeoutRef.current) { clearTimeout(autoStopTimeoutRef.current); autoStopTimeoutRef.current = null; }
    try { processorRef.current?.disconnect(); } catch { /* already disconnected */ }
    try { sourceRef.current?.disconnect(); } catch { /* already disconnected */ }
    try { gainRef.current?.disconnect(); } catch { /* already disconnected */ }
    processorRef.current = null;
    sourceRef.current = null;
    gainRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop()); // turns off the mic indicator
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
  }, []);

  useEffect(() => () => teardownAudioGraph(), [teardownAudioGraph]);

  // stop() has no reactive dependencies at all — built once, referenced
  // by identity from start()'s auto-stop timer without ever going stale.
  const stop = useCallback(async () => {
    if (phaseRef.current !== 'recording') {
      return keepAudioRef.current ? { blob: null, durationSeconds: 0 } : '';
    }
    phaseRef.current = 'transcribing'; // set before any await — blocks a near-simultaneous double stop() (manual click racing the auto-stop timer)

    const nativeSampleRate = audioCtxRef.current?.sampleRate || 48000;
    const totalLength = chunksRef.current.reduce((n, c) => n + c.length, 0);
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunksRef.current) { merged.set(chunk, offset); offset += chunk.length; }

    teardownAudioGraph();
    chunksRef.current = [];

    if (totalLength === 0) {
      setPhase('idle');
      setElapsedMs(0);
      setWaveform(new Array(WAVEFORM_HISTORY_LENGTH).fill(0));
      return keepAudioRef.current ? { blob: null, durationSeconds: 0 } : '';
    }

    // Sample-count based, not elapsedMs-based -- elapsedMs is driven by a
    // setInterval and is a display approximation, this is exact against
    // what was actually captured. Callers round it themselves (the
    // reflections.duration_seconds column is an integer;
    // voiceNoteStorage.js already does Math.round on the way in). Computed
    // from the NATIVE rate/length, before any resampling below -- resampling
    // changes the sample count but not the real-world duration it represents.
    const durationSeconds = totalLength / nativeSampleRate;

    // Downsample for the transcribe-and-discard path only (see
    // TRANSCRIBE_SAMPLE_RATE_HZ above). start() already asks the
    // AudioContext for 16kHz on that path -- this only does real work when
    // a browser refused that request and handed back its own native rate
    // instead, which is why the check below usually short-circuits to a
    // no-op copy. keepAudio recordings (the journal) skip this entirely
    // and keep native quality, since they're stored for playback, not
    // uploaded to Google's sync recognize.
    let pcmSamples = merged;
    let wavSampleRate = nativeSampleRate;
    if (!keepAudioRef.current && nativeSampleRate !== TRANSCRIBE_SAMPLE_RATE_HZ) {
      pcmSamples = resampleFloat32(merged, nativeSampleRate, TRANSCRIBE_SAMPLE_RATE_HZ);
      wavSampleRate = TRANSCRIBE_SAMPLE_RATE_HZ;
    }
    const wavBlob = encodeWav(pcmSamples, wavSampleRate);

    if (keepAudioRef.current) {
      // The journal's 3-minute recordings never reach voice-to-text from
      // here at all -- that function's own MAX_AUDIO_BYTES/60s
      // sync-recognize budget cannot take a recording this long.
      // Transcribing it is a separate, not-yet-built long-running flow
      // the caller drives itself once this blob is uploaded (ticket 6b,
      // spec 9.3) -- not something stop() can do inline the way the
      // short AddBook recordings do below.
      setPhase('idle');
      setElapsedMs(0);
      setWaveform(new Array(WAVEFORM_HISTORY_LENGTH).fill(0));
      return { blob: wavBlob, durationSeconds };
    }

    setPhase('transcribing');
    try {
      const audio_base64 = await blobToBase64(wavBlob);

      // Outer deadline strictly over the edge function's own sequential
      // inner budgets (OAuth exchange, then recognize), same shape as
      // book-search's client/upstream pair — see the constants above.
      const timeoutMs = transcribeTimeoutMsRef.current;
      const invokeTimeoutMs =
        GOOGLE_OAUTH_TIMEOUT_MS_MIRROR + timeoutMs + INVOKE_ROUND_TRIP_MARGIN_MS;

      const { data, error: invokeError } = await supabase.functions.invoke('voice-to-text', {
        body: { audio_base64, mime_type: 'audio/wav', recognize_timeout_ms: timeoutMs },
        timeout: invokeTimeoutMs,
      });

      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      setPhase('idle');
      setElapsedMs(0);
      setWaveform(new Array(WAVEFORM_HISTORY_LENGTH).fill(0));

      if (data?.status === 'empty') {
        setError("Didn't catch that, try again.");
        return '';
      }
      return data?.transcript || '';
    } catch (err) {
      console.error('useVoiceRecorder: transcription failed:', err?.message || err);
      setError('Transcription failed. You can type instead.');
      setPhase('error');
      return '';
    }
  }, [teardownAudioGraph, setPhase]);

  const start = useCallback(async () => {
    if (phaseRef.current === 'recording' || phaseRef.current === 'transcribing') return;
    setError(null);
    chunksRef.current = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access was denied or unavailable.');
      setPhase('error');
      return;
    }
    streamRef.current = stream;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    // keepAudio (the journal) is stored for playback -- native hardware
    // quality, unchanged from before. The transcribe-and-discard path
    // (default) asks for 16kHz directly, since that's all Google's sync
    // recognize wants anyway; a browser that rejects an explicit rate
    // (throws instead of silently ignoring it) falls back to native, and
    // stop() resamples down before encoding so the payload savings land
    // either way.
    let audioCtx;
    if (keepAudioRef.current) {
      audioCtx = new AudioCtx();
    } else {
      try {
        audioCtx = new AudioCtx({ sampleRate: TRANSCRIBE_SAMPLE_RATE_HZ });
      } catch {
        audioCtx = new AudioCtx();
      }
    }
    audioCtxRef.current = audioCtx;

    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;

    // 4096-sample buffer is the standard middle ground: small enough for
    // a reasonably prompt stop, large enough not to spend all its time in
    // callback overhead.
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;
    processor.onaudioprocess = (e) => {
      // Float32Array from onaudioprocess is reused by the browser after
      // this callback returns — copy it, don't keep the reference.
      const channelData = e.inputBuffer.getChannelData(0);
      chunksRef.current.push(new Float32Array(channelData));

      // RMS of this callback's samples as a live amplitude level, clamped
      // to 0..1 (samples are already -1..1, so RMS should not exceed 1,
      // but clamped anyway rather than trusting that on every browser).
      // A cheap, standard "how loud right now" measure -- not a spectral
      // analysis, just enough for a live waveform bar to move. Written to
      // a ref, not state: the 250ms tick below is what turns this into a
      // render, so 48000/4096 ≈ 11.7 callbacks/sec do not each cause one.
      let sumSquares = 0;
      for (let i = 0; i < channelData.length; i++) sumSquares += channelData[i] * channelData[i];
      levelRef.current = Math.min(1, Math.sqrt(sumSquares / channelData.length));
    };

    // ScriptProcessorNode only fires onaudioprocess while connected
    // through to a destination. Routed through a zero-gain node so
    // nothing is actually audible — this is a processing tap, not
    // playback.
    const gain = audioCtx.createGain();
    gain.gain.value = 0;
    gainRef.current = gain;

    source.connect(processor);
    processor.connect(gain);
    gain.connect(audioCtx.destination);

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setWaveform(new Array(WAVEFORM_HISTORY_LENGTH).fill(0));
    tickIntervalRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
      setWaveform(prev => {
        const next = prev.slice(1);
        next.push(levelRef.current);
        return next;
      });
    }, 250);
    autoStopTimeoutRef.current = setTimeout(() => { stop(); }, maxDurationMsRef.current);

    setPhase('recording');
  }, [setPhase, stop]);

  const cancel = useCallback(() => {
    teardownAudioGraph();
    chunksRef.current = [];
    setPhase('idle');
    setElapsedMs(0);
    setWaveform(new Array(WAVEFORM_HISTORY_LENGTH).fill(0));
  }, [teardownAudioGraph, setPhase]);

  return { state, error, elapsedMs, waveform, maxDurationMs, start, stop, cancel };
}
